import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import { College, DEFAULT_QUIZ_REMINDER, DEFAULT_ARTICLE_REMINDER } from '@mightybyte/business';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Bell, Send, Plus, Pencil, Trash2, FlaskConical } from 'lucide-react';

const COLLEGE_LABELS: Record<College, string> = {
    [College.LMU]: 'LMU',
    [College.Lehigh]: 'Lehigh University',
};

type ViewFilter = 'reminder' | 'custom';

export function NotificationsScreen() {
    const [viewFilter, setViewFilter] = useState<ViewFilter>('reminder');

    return (
        <div className="space-y-6 pb-8">
            {/* View toggle buttons */}
            <div className="inline-flex rounded-lg border border-gray-300 p-1 bg-gray-50">
                <button
                    onClick={() => setViewFilter('reminder')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        viewFilter === 'reminder'
                            ? 'bg-brand text-white shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                    }`}
                >
                    Reminder
                </button>
                <button
                    onClick={() => setViewFilter('custom')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        viewFilter === 'custom'
                            ? 'bg-brand text-white shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                    }`}
                >
                    Custom
                </button>
            </div>

            {/* Content based on selected view */}
            {viewFilter === 'reminder' ? (
                <ReminderConfigSection />
            ) : (
                <CustomNotificationsSection />
            )}
        </div>
    );
}

// ─── Reminder Configuration ──────────────────────────────────────────────────

function ReminderConfigSection() {
    const utils = trpc.useUtils();
    const { data: config, isLoading } = trpc.admin.notification.getConfig.useQuery();
    const updateConfig = trpc.admin.notification.updateConfig.useMutation({
        onSuccess: () => {
            utils.admin.notification.getConfig.invalidate();
            toast.success('Reminder configuration updated');
        },
        onError: (err) => toast.error(err.message),
    });

    const [showTestModal, setShowTestModal] = useState(false);

    if (isLoading || !config) {
        return <div className="animate-pulse h-48 bg-gray-100 rounded-lg" />;
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                        <Bell className="w-5 h-5" />
                        Reminder Notifications
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Automated reminders for quiz completion and article reviews
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <ToggleSwitch
                        checked={config.remindersEnabled}
                        onChange={(checked) =>
                            updateConfig.mutate({ remindersEnabled: checked })
                        }
                        label={config.remindersEnabled ? 'Enabled' : 'Disabled'}
                    />
                </div>
            </div>

            {config.remindersEnabled && (
                <div className="grid grid-cols-1  gap-6">

                    <ReminderContentCard
                        config={config}
                        onSave={(data) => updateConfig.mutate(data)}
                        isPending={updateConfig.isPending}
                    />
                    <CronExpressionCard
                        cronExpression={config.cronExpression}
                        cronDescription={config.cronDescription}
                        onSave={(expr, desc) => updateConfig.mutate({
                            cronExpression: expr,
                            cronDescription: desc
                        })}
                        isPending={updateConfig.isPending}
                    />
                </div>
            )}

            {showTestModal && (
                <TestNotificationModal
                    config={config}
                    onClose={() => setShowTestModal(false)}
                />
            )}
        </div>
    );
}

function CronExpressionCard({
    cronExpression,
    cronDescription,
    onSave,
    isPending,
}: {
    cronExpression: string;
    cronDescription: string;
    onSave: (expr: string, desc: string) => void;
    isPending: boolean;
}) {
    const [value, setValue] = useState(cronExpression);
    const [description, setDescription] = useState(cronDescription);

    const hasChanges = value !== cronExpression || description !== cronDescription;

    return (
        <Card className="p-4 gap-0">
            <div className={'flex flex-row gap-4 w-full'}>
                <div className={'w-full'}>
                    <Label className="text-sm font-medium">Schedule (Cron Expression)</Label>
                    <Input
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        placeholder={cronExpression}
                        className="mt-1"
                    />
                </div>
                <div className={'w-full'}>
                    <Label className="text-sm font-medium">Description</Label>
                    <Input
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="e.g., Every day at 12pm PDT / 7PM UTC"
                        className="mt-1"
                    />
                </div>

            </div>

            <p className="text-xs text-gray-500 mt-1 mb-4">
                Scheduler uses America/Los Angeles timezone.
            </p>
            <Button
                size="sm"
                disabled={isPending || !hasChanges}
                onClick={() => onSave(value, description)}
            >
                Update Schedule
            </Button>
        </Card>
    );
}

// Reusable badge component
function ReminderBadge({
    isActive,
    label,
}: {
    isActive: boolean;
    label: string;
}) {
    return (
        <div className={`absolute -top-3 left-3 px-2 py-1 bg-white rounded-xl border flex items-center ${
            isActive ? 'border-brand' : 'border-gray-400'
        }`}>
            <span className={`text-xs font-semibold uppercase tracking-wide ${
                isActive ? 'text-brand' : 'text-gray-400'
            }`}>
                {label}
            </span>
        </div>
    );
}

function ReminderContentCard({
    config,
    onSave,
    isPending,
}: {
    config: {
        useCustomReminderContent: boolean;
        customQuizReminder?: { title: string; body: string };
        customArticleReminder?: { title: string; body: string };
    };
    onSave: (data: any) => void;
    isPending: boolean;
}) {
    const [useCustom, setUseCustom] = useState(config.useCustomReminderContent);
    const [quizTitle, setQuizTitle] = useState(config.customQuizReminder?.title ?? '');
    const [quizBody, setQuizBody] = useState(config.customQuizReminder?.body ?? '');
    const [articleTitle, setArticleTitle] = useState(config.customArticleReminder?.title ?? '');
    const [articleBody, setArticleBody] = useState(config.customArticleReminder?.body ?? '');
    const [testModalType, setTestModalType] = useState<'quiz' | 'article' | null>(null);

    const handleSave = () => {
        const data: any = { useCustomReminderContent: useCustom };
        if (useCustom) {
            if (quizTitle && quizBody) {
                data.customQuizReminder = { title: quizTitle, body: quizBody };
            }
            if (articleTitle && articleBody) {
                data.customArticleReminder = { title: articleTitle, body: articleBody };
            }
        }
        onSave(data);
    };

    return (
        <Card className="p-4 gap-0">
            <div>
                <div className="flex items-center justify-between">
                    <ToggleSwitch
                        checked={useCustom}
                        onChange={setUseCustom}
                        label={useCustom ? 'Use Custom: ON' : 'Use Custom: OFF'}
                    />
                    <Button size="sm" disabled={isPending} onClick={handleSave}>
                        Save Content Settings
                    </Button>
                </div>

                <div className="mt-3 flex flex-col md:flex-row gap-4">
                    {/* Quiz Reminder Section */}
                    <div className="space-y-4 flex-1">
                        <div className="flex items-center justify-between">
                            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Quiz Reminder</p>
                        </div>

                        {/* Default Content */}
                        <div className={`mb-6 relative rounded-lg border-2 transition-all ${
                            !useCustom
                                ? 'border-brand bg-brand/5'
                                : 'border-gray-200 bg-gray-50'
                        }`}>
                            <ReminderBadge
                                isActive={!useCustom}
                                label={`Default ${!useCustom ? '(Active)' : ''}`}
                            />
                            <div className="mt-3 p-3 space-y-1">
                                <p className={`text-sm font-medium ${
                                    !useCustom ? 'text-gray-900' : 'text-gray-600'
                                }`}>
                                    {DEFAULT_QUIZ_REMINDER.title}
                                </p>
                                <p className={`text-sm ${
                                    !useCustom ? 'text-gray-700' : 'text-gray-500'
                                }`}>
                                    {DEFAULT_QUIZ_REMINDER.body}
                                </p>
                            </div>
                        </div>

                        {/* Custom Content */}
                        <div className={`relative rounded-lg border-2 transition-all ${
                            useCustom
                                ? 'border-brand bg-brand/5'
                                : 'border-gray-200 bg-gray-50'
                        }`}>
                            <ReminderBadge
                                isActive={useCustom}
                                label={`Custom ${useCustom ? '(Active)' : ''}`}
                            />
                            <div className="mt-3 p-3 space-y-2">
                                <Input
                                    value={quizTitle}
                                    onChange={(e) => setQuizTitle(e.target.value)}
                                    placeholder="Custom quiz reminder title"
                                    className={!useCustom ? 'bg-white/50' : ''}
                                />
                                <textarea
                                    className={`flex w-full rounded-md border border-input px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-h-[60px] resize-y ${
                                        !useCustom ? 'bg-white/50' : 'bg-background'
                                    }`}
                                    value={quizBody}
                                    onChange={(e) => setQuizBody(e.target.value)}
                                    placeholder="Custom quiz reminder body"
                                />
                            </div>
                        </div>

                        <Button
                            className={'w-full'}
                            variant="outline"
                            size="sm"
                            onClick={() => setTestModalType('quiz')}
                        >
                            <FlaskConical className="w-3.5 h-3.5 mr-1" />
                            Test Quiz Reminder
                        </Button>
                    </div>

                    {/* Article Reminder Section */}
                    <div className="space-y-4 flex-1">
                        <div className="flex items-center justify-between">
                            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Article Reminder</p>
                        </div>

                        {/* Default Content */}
                        <div className={`mb-6 relative rounded-lg border-2 transition-all ${
                            !useCustom
                                ? 'border-brand bg-brand/5'
                                : 'border-gray-200 bg-gray-50'
                        }`}>
                            <ReminderBadge
                                isActive={!useCustom}
                                label={`Default ${!useCustom ? '(Active)' : ''}`}
                            />
                            <div className="mt-3 p-3 space-y-1">
                                <p className={`text-sm font-medium ${
                                    !useCustom ? 'text-gray-900' : 'text-gray-600'
                                }`}>
                                    {DEFAULT_ARTICLE_REMINDER.title}
                                </p>
                                <p className={`text-sm ${
                                    !useCustom ? 'text-gray-700' : 'text-gray-500'
                                }`}>
                                    {DEFAULT_ARTICLE_REMINDER.body}
                                </p>
                            </div>
                        </div>

                        {/* Custom Content */}
                        <div className={`relative rounded-lg border-2 transition-all ${
                            useCustom
                                ? 'border-brand bg-brand/5'
                                : 'border-gray-200 bg-gray-50'
                        }`}>
                            <ReminderBadge
                                isActive={useCustom}
                                label={`Custom ${useCustom ? '(Active)' : ''}`}
                            />
                            <div className="mt-3 p-3 space-y-2">
                                <Input
                                    value={articleTitle}
                                    onChange={(e) => setArticleTitle(e.target.value)}
                                    placeholder="Custom article reminder title"
                                    className={!useCustom ? 'bg-white/50' : ''}
                                />
                                <textarea
                                    className={`flex w-full rounded-md border border-input px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-h-[60px] resize-y ${
                                        !useCustom ? 'bg-white/50' : 'bg-background'
                                    }`}
                                    value={articleBody}
                                    onChange={(e) => setArticleBody(e.target.value)}
                                    placeholder="Custom article reminder body"
                                />
                            </div>
                        </div>
                        <Button
                            className={'w-full'}
                            variant="outline"
                            size="sm"
                            onClick={() => setTestModalType('article')}
                        >
                            <FlaskConical className="w-3.5 h-3.5 mr-1" />
                            Test Article reminder
                        </Button>
                    </div>
                </div>
            </div>


            <p className="text-xs text-gray-700 mt-2">
                Tip: Use <code className="bg-gray-100 px-1 rounded">{'{username}'}</code> in title or body to insert the user's first name.
            </p>

            {testModalType && (
                <TestReminderModal
                    type={testModalType}
                    config={config}
                    onClose={() => setTestModalType(null)}
                />
            )}
        </Card>
    );
}

// Simplified test modal for quiz/article reminders - only asks for email
function TestReminderModal({
    type,
    config,
    onClose,
}: {
    type: 'quiz' | 'article';
    config: {
        useCustomReminderContent: boolean;
        customQuizReminder?: { title: string; body: string };
        customArticleReminder?: { title: string; body: string };
    };
    onClose: () => void;
}) {
    const [email, setEmail] = useState('');

    // Get the appropriate reminder content based on type and settings
    const reminder = type === 'quiz'
        ? (config.useCustomReminderContent && config.customQuizReminder
            ? config.customQuizReminder
            : DEFAULT_QUIZ_REMINDER)
        : (config.useCustomReminderContent && config.customArticleReminder
            ? config.customArticleReminder
            : DEFAULT_ARTICLE_REMINDER);

    const sendTest = trpc.admin.notification.sendTestNotification.useMutation({
        onSuccess: () => {
            toast.success(`Test ${type} reminder sent!`);
            onClose();
        },
        onError: (err) => toast.error(err.message),
    });

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Test {type === 'quiz' ? 'Quiz' : 'Article'} Reminder</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label>User Email</Label>
                        <Input
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="user@example.com"
                            autoFocus
                        />
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                        <p className="text-xs font-medium text-gray-600 mb-2">Preview:</p>
                        <p className="text-sm font-medium text-gray-900">{reminder.title}</p>
                        <p className="text-sm text-gray-700 mt-1">{reminder.body}</p>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button
                        disabled={!email || sendTest.isPending}
                        onClick={() => sendTest.mutate({ email, title: reminder.title, body: reminder.body })}
                    >
                        <Send className="w-4 h-4 mr-1" />
                        Send Test
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function TestNotificationModal({
    config,
    onClose,
}: {
    config: {
        useCustomReminderContent: boolean;
        customQuizReminder?: { title: string; body: string };
        customArticleReminder?: { title: string; body: string };
    };
    onClose: () => void;
}) {
    const [email, setEmail] = useState('');
    const [reminderType, setReminderType] = useState<'quiz' | 'article'>('quiz');

    // Get the appropriate reminder content based on settings
    const getDefaultReminder = () => {
        if (reminderType === 'quiz') {
            return config.useCustomReminderContent && config.customQuizReminder
                ? config.customQuizReminder
                : DEFAULT_QUIZ_REMINDER;
        } else {
            return config.useCustomReminderContent && config.customArticleReminder
                ? config.customArticleReminder
                : DEFAULT_ARTICLE_REMINDER;
        }
    };

    const defaultReminder = getDefaultReminder();
    const [title, setTitle] = useState(defaultReminder.title);
    const [body, setBody] = useState(defaultReminder.body);


    const sendTest = trpc.admin.notification.sendTestNotification.useMutation({
        onSuccess: () => {
            toast.success('Test notification sent!');
            onClose();
        },
        onError: (err) => toast.error(err.message),
    });

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Send Test Notification</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label>User Email</Label>
                        <Input
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="user@example.com"
                        />
                    </div>
                    <div>
                        <Label>Title</Label>
                        <Input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                    </div>
                    <div>
                        <Label>Body</Label>
                        <Input
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                        />
                    </div>
                    <p className="text-xs text-gray-700">
                        Tip: Use <code className="bg-gray-100 px-1 rounded">{'{username}'}</code> to insert the user's first name.
                    </p>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button
                        disabled={!email || !title || !body || sendTest.isPending}
                        onClick={() => sendTest.mutate({ email, title, body })}
                    >
                        <Send className="w-4 h-4 mr-1" />
                        Send
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ─── Custom Notifications ────────────────────────────────────────────────────

function CustomNotificationsSection() {
    const utils = trpc.useUtils();
    const { data: notifications, isLoading } = trpc.admin.notification.getCustomNotifications.useQuery();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [testNotification, setTestNotification] = useState<{ title: string; body: string } | null>(null);

    const deleteMutation = trpc.admin.notification.deleteCustomNotification.useMutation({
        onSuccess: () => {
            utils.admin.notification.getCustomNotifications.invalidate();
            toast.success('Notification deleted');
        },
        onError: (err) => toast.error(err.message),
    });

    const sendMutation = trpc.admin.notification.sendCustomNotification.useMutation({
        onSuccess: (data) => {
            toast.success(`Notification sent to ${data.recipientCount} users`);
        },
        onError: (err) => toast.error(err.message),
    });

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                        <Send className="w-5 h-5" />
                        Custom Notifications
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Create and send targeted notifications to specific groups
                    </p>
                </div>
                <Button onClick={() => setShowCreateModal(true)}>
                    <Plus className="w-4 h-4 mr-1" />
                    New Notification
                </Button>
            </div>

            {isLoading && (
                <div className="animate-pulse space-y-3">
                    {[1, 2].map((i) => (
                        <div key={i} className="h-24 bg-gray-100 rounded-lg" />
                    ))}
                </div>
            )}

            {notifications && notifications.length === 0 && (
                <Card className="p-12 text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Bell className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Custom Notifications</h3>
                    <p className="text-gray-500 mb-4">Create your first notification to get started.</p>
                    <Button onClick={() => setShowCreateModal(true)}>
                        Create Notification
                    </Button>
                </Card>
            )}

            {notifications && notifications.length > 0 && (
                <div className="space-y-3">
                    {notifications.map((notification) => (
                        <Card key={notification.id} className="p-4">
                            <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-medium text-gray-900 truncate">
                                        {notification.title}
                                    </h3>
                                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                                        {notification.body}
                                    </p>
                                    <div className="flex items-center gap-2 mt-2">
                                        {notification.targetColleges.map((college) => (
                                            <span
                                                key={college}
                                                className="text-xs bg-brand/10 text-brand px-2 py-0.5 rounded-full font-medium"
                                            >
                                                {COLLEGE_LABELS[college as College]}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 ml-4 shrink-0">
                                    <Button
                                        variant="ghost"
                                        size="icon-sm"
                                        onClick={() => setTestNotification({ title: notification.title, body: notification.body })}
                                        title="Test notification"
                                    >
                                        <FlaskConical className="w-4 h-4" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon-sm"
                                        onClick={() => setEditingId(notification.id)}
                                    >
                                        <Pencil className="w-4 h-4" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon-sm"
                                        className="text-red-500 hover:text-red-700"
                                        onClick={() => {
                                            if (confirm('Delete this notification?')) {
                                                deleteMutation.mutate({ id: notification.id });
                                            }
                                        }}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                    <Button
                                        size="sm"
                                        onClick={() => {
                                            if (confirm('Send this notification to all targeted users?')) {
                                                sendMutation.mutate({ id: notification.id });
                                            }
                                        }}
                                        disabled={sendMutation.isPending}
                                    >
                                        <Send className="w-4 h-4 mr-1" />
                                        Send
                                    </Button>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            {showCreateModal && (
                <CustomNotificationModal
                    onClose={() => setShowCreateModal(false)}
                />
            )}

            {editingId && (
                <CustomNotificationModal
                    editId={editingId}
                    onClose={() => setEditingId(null)}
                />
            )}

            {testNotification && (
                <SimpleTestModal
                    title={testNotification.title}
                    body={testNotification.body}
                    onClose={() => setTestNotification(null)}
                />
            )}
        </div>
    );
}

// Simple test modal for custom notifications - only asks for email, shows preview
function SimpleTestModal({
    title,
    body,
    onClose,
}: {
    title: string;
    body: string;
    onClose: () => void;
}) {
    const [email, setEmail] = useState('');

    const sendTest = trpc.admin.notification.sendTestNotification.useMutation({
        onSuccess: () => {
            toast.success('Test notification sent!');
            onClose();
        },
        onError: (err) => toast.error(err.message),
    });

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Test Custom Notification</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label>User Email</Label>
                        <Input
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="user@example.com"
                            autoFocus
                        />
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                        <p className="text-xs font-medium text-gray-600 mb-2">Preview:</p>
                        <p className="text-sm font-medium text-gray-900">{title}</p>
                        <p className="text-sm text-gray-700 mt-1">{body}</p>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button
                        disabled={!email || sendTest.isPending}
                        onClick={() => sendTest.mutate({ email, title, body })}
                    >
                        <Send className="w-4 h-4 mr-1" />
                        Send Test
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function CustomNotificationModal({
    editId,
    onClose,
}: {
    editId?: string;
    onClose: () => void;
}) {
    const utils = trpc.useUtils();
    const existingNotifications = trpc.admin.notification.getCustomNotifications.useQuery();
    const existing = editId
        ? existingNotifications.data?.find((n) => n.id === editId)
        : null;

    const [title, setTitle] = useState(existing?.title ?? '');
    const [body, setBody] = useState(existing?.body ?? '');
    const [targetColleges, setTargetColleges] = useState<College[]>(
        (existing?.targetColleges as College[]) ?? [],
    );

    const createMutation = trpc.admin.notification.createCustomNotification.useMutation({
        onSuccess: () => {
            utils.admin.notification.getCustomNotifications.invalidate();
            toast.success('Notification created');
            onClose();
        },
        onError: (err) => toast.error(err.message),
    });

    const updateMutation = trpc.admin.notification.updateCustomNotification.useMutation({
        onSuccess: () => {
            utils.admin.notification.getCustomNotifications.invalidate();
            toast.success('Notification updated');
            onClose();
        },
        onError: (err) => toast.error(err.message),
    });

    const toggleCollege = (college: College) => {
        setTargetColleges((prev) =>
            prev.includes(college)
                ? prev.filter((c) => c !== college)
                : [...prev, college],
        );
    };

    const handleSubmit = () => {
        if (editId) {
            updateMutation.mutate({ id: editId, title, body, targetColleges });
        } else {
            createMutation.mutate({ title, body, targetColleges });
        }
    };

    const isPending = createMutation.isPending || updateMutation.isPending;
    const isValid = title.trim() && body.trim() && targetColleges.length > 0;

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>
                        {editId ? 'Edit Notification' : 'New Custom Notification'}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label>Title</Label>
                        <Input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Notification title"
                        />
                    </div>
                    <div>
                        <Label>Body</Label>
                        <textarea
                            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-h-[80px] resize-y"
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            placeholder="Notification body text"
                        />
                    </div>
                    <div>
                        <Label className="mb-2 block">Target Groups</Label>
                        <div className="flex gap-4">
                            {Object.values(College).map((college) => (
                                <label
                                    key={college}
                                    className="flex items-center gap-2 cursor-pointer"
                                >
                                    <Checkbox
                                        checked={targetColleges.includes(college)}
                                        onCheckedChange={() => toggleCollege(college)}
                                    />
                                    <span className="text-sm">{COLLEGE_LABELS[college]}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <p className="text-xs text-gray-400">
                        Tip: Use <code className="bg-gray-100 px-1 rounded">{'{ username }'}</code> in title or body to insert the user's first name.
                    </p>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button
                        disabled={!isValid || isPending}
                        onClick={handleSubmit}
                    >
                        {editId ? 'Update' : 'Save'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ─── Shared Toggle Component ─────────────────────────────────────────────────

function ToggleSwitch({
    checked,
    onChange,
    label,
}: {
    checked: boolean;
    onChange: (checked: boolean) => void;
    label?: string;
}) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            onClick={() => onChange(!checked)}
            className="flex items-center gap-2 cursor-pointer"
        >
            {label && (
                <span className={`text-sm font-medium ${checked ? 'text-brand' : 'text-gray-500'}`}>
                    {label}
                </span>
            )}
            <span
                className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors ${
                    checked ? 'bg-brand' : 'bg-gray-200'
                }`}
            >
                <span
                    className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                        checked ? 'translate-x-5' : 'translate-x-0'
                    }`}
                />
            </span>
        </button>
    );
}
